import { forgotPasswordRequestSchema, type ForgotPasswordRequest } from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@instanexus/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { CaptchaField } from '@/components/captcha-field';
import { useApiClient } from '@/hooks/use-api-client';
import { useCaptcha } from '@/hooks/use-captcha';
import { centralLoginPath, resetPasswordPath } from '@/lib/auth-paths';
import { parseSignInRequest } from '@/lib/sign-in-request';

/** Same fallback reasoning as `login-page.tsx` — never a declared `ErrorCode`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

/** The token is held by `useCaptcha`, not by a form field. */
type ForgotPasswordFormValues = Omit<ForgotPasswordRequest, 'captchaToken'>;

/**
 * A pasted address routinely carries whitespace, and the field is controlled, so
 * normalise in front of the schema — the SAME value is then validated and sent.
 */
const normalizeValues = (values: ForgotPasswordFormValues): ForgotPasswordFormValues => ({
  email: values.email.trim(),
});

/**
 * Step one of password recovery: ask for a reset code.
 *
 * Public by construction — a user who cannot sign in is the only person who ever
 * reaches it — so it is registered outside `RequireAuth`, next to `/login`.
 *
 * THE CONFIRMATION IS NEUTRAL, AND THAT IS THE POINT. `POST
 * /api/web/auth/forgot-password` answers 204 for every syntactically valid
 * address, known or not, so this screen must never say "we sent you an email" or
 * "no such account" — either phrasing turns the form into an account-existence
 * oracle that anyone can query. The copy says "IF that address has an account",
 * and the unknown-address path renders the identical block, asserted in
 * `e2e/password-reset.e2e.ts`.
 *
 * Its states: idle, submitting (`formState.isSubmitting`, which disables the
 * button so a double-click cannot send two codes), error (one translated message
 * for the error CODE — a rate limit is the realistic one here), and sent.
 */
export function ForgotPasswordPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { client } = parseSignInRequest(searchParams);

  /** One message for any invalid field — the only translated copy we have. */
  const validationMessage = errorMessage(t, 'VALIDATION_FAILED');

  const resolver = useMemo<Resolver<ForgotPasswordFormValues>>(() => {
    const validate = zodResolver(forgotPasswordRequestSchema.omit({ captchaToken: true }), {
      path: [],
      async: true,
      errorMap: () => ({ message: validationMessage }),
    });
    return (values, context, options) => validate(normalizeValues(values), context, options);
  }, [validationMessage]);

  const captcha = useCaptcha();

  const form = useForm<ForgotPasswordFormValues>({ resolver, defaultValues: { email: '' } });

  const onSubmit = form.handleSubmit(async (values: ForgotPasswordFormValues): Promise<void> => {
    setErrorCode(null);
    if (captcha.token === null) return;
    const { email } = normalizeValues(values);
    try {
      await api.auth.forgotPassword({ email, captchaToken: captcha.token });
      setSentToEmail(email);
    } catch (error: unknown) {
      setErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
    } finally {
      // Single-use token: re-arm after every attempt, whatever happened.
      captcha.reset();
    }
  });

  const isSubmitting = form.formState.isSubmitting;

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        {sentToEmail === null ? (
          <>
            <CardHeader>
              <h1 className="text-3xl font-semibold text-primary text-balance">
                {t('forgotPassword.title')}
              </h1>
              <CardDescription>{t('forgotPassword.description')}</CardDescription>
            </CardHeader>

            <Form {...form}>
              <form
                noValidate
                onSubmit={(event) => {
                  void onSubmit(event);
                }}
              >
                <CardContent className="flex flex-col gap-4">
                  {errorCode === null ? null : (
                    <p role="alert" className="text-danger text-sm text-pretty">
                      {errorMessage(t, errorCode)}
                    </p>
                  )}

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('forgotPassword.emailLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            inputMode="email"
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck={false}
                            width="full"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <CaptchaField captcha={captcha} />
                </CardContent>

                <CardFooter className="flex flex-col items-stretch gap-3">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting || captcha.token === null}
                    aria-busy={isSubmitting}
                  >
                    {isSubmitting ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
                  </Button>
                  <Button asChild variant="link" className="w-full">
                    <Link to={centralLoginPath(client)}>{t('forgotPassword.backToLogin')}</Link>
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </>
        ) : (
          <>
            <CardHeader>
              <h1 className="text-3xl font-semibold text-primary text-balance">
                {t('forgotPassword.title')}
              </h1>
            </CardHeader>
            <CardContent>
              {/*
                Deliberately says nothing about whether the address is known — see
                the component header. `Alert`'s default role is `status`, so the
                confirmation is announced without interrupting.
              */}
              <Alert>
                <AlertTitle>{t('forgotPassword.sentTitle')}</AlertTitle>
                <AlertDescription>{t('forgotPassword.sentDescription')}</AlertDescription>
              </Alert>
            </CardContent>
            <CardFooter className="flex flex-col items-stretch gap-3">
              <Button asChild className="w-full">
                <Link to={resetPasswordPath(client, sentToEmail)}>
                  {t('forgotPassword.sentContinue')}
                </Link>
              </Button>
              <Button asChild variant="link" className="w-full">
                <Link to={centralLoginPath(client)}>{t('forgotPassword.backToLogin')}</Link>
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </AuthCardLayout>
  );
}
